// src/pages/User/ViolationForm.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Card } from "react-bootstrap";
import axios from "axios";
import CompanyAddress from "../../components/CompanyAddress";
import logo from "../../images/logo.png";

const violationTypeOptions = [
  "A Warning",
  "Absence",
  "Company Policy",
  "Failure to Follow Procedure",
  "Failure to Notify",
  "HOS Violation",
  "Other",
  "Ticket Citation",
];

const trailerTypeOptions = [
  "Chassis",
  "Containers",
  "Dry Van",
  "Flatbed",
  "Heated",
  "Other",
  "Refrigerated",
  "Tractor unit",
];

const locationTypeOptions = ["Customer", "Org. Unit", "Other", "Road"];
const reportedByTypeOptions = ["Company Representative", "Other"];
const witnessTypeOptions = ["Staff", "Other"];

const ViolationForm = () => {
  const navigate = useNavigate();
  const { id: paramId, violId } = useParams();
  const isNew = !violId;
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [violation, setViolation] = useState({
    viol_type: "",
    viol_truck_number: "",
    viol_reported_date: "",
    viol_date: "",
    viol_trailers: [],
    viol_notes: [],
    viol_location_type: "",
    viol_location_name: "",
    viol_location_contact: "",
    viol_location_phone: "",
    viol_location_postal: "",
    viol_location_city: "",
    viol_location_province: "",
    viol_location_country: "",
    viol_reported_by_type: "",
    viol_reported_by_name: "",
    viol_reported_by_email: "",
    viol_reported_by_phone: "",
    viol_reported_by_id_number: "",
    viol_reported_notes: [],
    viol_witnesses: [],
  });

  const [attachments, setAttachments] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);
  const [isDeletingAttachmentId, setIsDeletingAttachmentId] = useState(null);

  // 🔹 Utility parser
  const safeParse = (val, fallback = []) => {
    try {
      if (Array.isArray(val)) return val;
      if (typeof val === "string") return JSON.parse(val || "[]");
      return fallback;
    } catch {
      return fallback;
    }
  };

  const handleDeleteSavedAttachment = async (attachmentId) => {
    const attachment = attachments.find((item) => item.id === attachmentId);
    const fileName =
      attachment?.va_original || attachment?.va_file || "attachment";

    if (!window.confirm(`Delete uploaded file "${fileName}"?`)) return;

    try {
      setIsDeletingAttachmentId(attachmentId);
      await axios.delete(`${API_BASE}/api/violations/attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setAttachments((prev) => prev.filter((item) => item.id !== attachmentId));
      setDeletedAttachmentIds((prev) => prev.filter((id) => id !== attachmentId));
    } catch (err) {
      console.error("Delete error", err);
      alert("Delete failed");
    } finally {
      setIsDeletingAttachmentId(null);
    }
  };

  const pendingAttachments = attachments.filter((item) => !item.id);
  const savedAttachments = attachments.filter((item) => item.id);

  // Load violation if editing
  useEffect(() => {
    if (!isNew) {
      axios
        .get(`${API_BASE}/api/violations/detail/${violId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
        .then(({ data }) => {
          console.log("📥 RAW VIOLATION DATA:", data);

          const reportedDate = data.viol_reported_date
            ? new Date(data.viol_reported_date).toISOString().slice(0, 10)
            : "";
          const violDate = data.viol_date
            ? new Date(data.viol_date).toISOString().slice(0, 10)
            : "";

          const trailers = safeParse(data.viol_trailers);
          const notes = safeParse(data.viol_notes);
          const reportedNotes = safeParse(data.viol_reported_notes);
          let witnesses = safeParse(data.viol_witnesses);
          witnesses = witnesses.map((w) => ({
            ...w,
            witnesses_notes: safeParse(w.witnesses_notes),
          }));

          console.log("✅ PARSED trailers:", trailers);

          setViolation((prev) => ({
            ...prev,
            ...data,
            viol_reported_date: reportedDate,
            viol_date: violDate,
            viol_trailers: trailers,
            viol_notes: notes,
            viol_reported_notes: reportedNotes,
            viol_witnesses: witnesses,
          }));
        })
        .catch((err) => console.error("❌ Error loading violation", err));
    }
  }, [isNew, violId, API_BASE]);

  useEffect(() => {
    if (!isNew && violId) {
      axios
        .get(`${API_BASE}/api/violations/attachments/${violId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
        .then((res) => {
          const normalized = res.data.map((a) => ({
            id: a.va_id,
            file: null,
            type: a.va_type,
            va_file: a.va_file,
            va_original: a.va_original,
          }));
          setAttachments(normalized);
        })
        .catch((err) => console.error("❌ Error fetching attachments", err));
    }
  }, [isNew, violId, API_BASE]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setViolation((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    const payload = {
      ...violation,
      viol_userid: userId,
      viol_trailers: violation.viol_trailers || [],
      viol_notes: violation.viol_notes || [],
      viol_reported_notes: violation.viol_reported_notes || [],
      viol_witnesses: violation.viol_witnesses || [],
    };

    try {
      let currentViolId = violId;
      if (violId && violId !== "new") {
        await axios.put(`${API_BASE}/api/violations/${violId}`, payload, {
          headers,
        });
        alert("✔️ Violation updated");
      } else {
        const res = await axios.post(`${API_BASE}/api/violations`, payload, {
          headers,
        });
        currentViolId = res.data.id;
        alert("✔️ Violation created");
      }

      // 🔹 Upload pending attachments (if any)
      if (attachments.length > 0) {
        for (const a of attachments) {
          if (!a.file) continue; // skip already-saved
          const formData = new FormData();
          formData.append("file", a.file);
          formData.append("viol_id", currentViolId);
          formData.append("viol_userid", userId);
          formData.append("va_type", a.type);

          await axios.post(
            `${API_BASE}/api/violations/attachments/upload?userId=${userId}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
        }
      }

      // Delete marked attachments
      for (const delId of deletedAttachmentIds) {
        await axios.delete(`${API_BASE}/api/violations/attachments/${delId}`, {
          headers,
        });
      }

      navigate(`/violations/${userId}/violation-detail/${currentViolId}`);
      window.location.reload();
    } catch (err) {
      console.error("❌ Save error", err);
      alert("Save failed");
    }
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>Violation</span>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => navigate(`/violations/${userId}`)}
          >
            View List
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Return
          </button>
        </div>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Violation</h2>
          </div>
          <CompanyAddress />
        </div>

        <Card>
          <Card.Body>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            {/* Violation Info */}
            <div className="card p-3 mb-3">
              <h5>Violation Information</h5>
              <div className="row mt-3">
                <div className="col-md-6 mb-2">
                  <label>Violation Type *</label>
                  <select
                    className="form-control"
                    name="viol_type"
                    value={violation.viol_type}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select</option>
                    {violationTypeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mb-2">
                  <label>Reported Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="viol_reported_date"
                    value={violation.viol_reported_date}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-3 mb-2">
                  <label>Violation Date </label>
                  <input
                    type="date"
                    className="form-control"
                    name="viol_date"
                    value={violation.viol_date}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 mb-2">
                  <label>Truck Number</label>
                  <input
                    className="form-control"
                    name="viol_truck_number"
                    value={violation.viol_truck_number}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Trailers */}
              <div className="d-flex justify-content-between align-items-center mb-2 mt-2">
                <h6>Trailers</h6>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() =>
                    setViolation({
                      ...violation,
                      viol_trailers: [
                        ...violation.viol_trailers,
                        { number: "", type: "" },
                      ],
                    })
                  }
                >
                  ➕ Add Trailer
                </button>
              </div>
              {violation.viol_trailers.map((t, idx) => (
                <div key={idx} className="row my-3">
                  <div className="col-md-5">
                    <input
                      className="form-control"
                      placeholder="Trailer Number"
                      value={t.number}
                      onChange={(e) => {
                        const updated = [...violation.viol_trailers];
                        updated[idx].number = e.target.value;
                        setViolation({ ...violation, viol_trailers: updated });
                      }}
                    />
                  </div>
                  <div className="col-md-5">
                    <select
                      className="form-control"
                      value={t.type}
                      onChange={(e) => {
                        const updated = [...violation.viol_trailers];
                        updated[idx].type = e.target.value;
                        setViolation({ ...violation, viol_trailers: updated });
                      }}
                    >
                      <option value="">Select Trailer Type</option>
                      {trailerTypeOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="text-end col-md-2">
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        const updated = violation.viol_trailers.filter(
                          (_, i) => i !== idx
                        );
                        setViolation({ ...violation, viol_trailers: updated });
                      }}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}

              {/* Location Info */}
              <h5 className="mt-3">Location Information</h5>
              <div className="row my-3">
                <div className="col-md-3">
                  <label>Location Type</label>
                  <select
                    className="form-control"
                    name="viol_location_type"
                    value={violation.viol_location_type}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {locationTypeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label>Location</label>
                  <input
                    className="form-control"
                    name="viol_location_name"
                    value={violation.viol_location_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-3">
                  <label>Contact</label>
                  <input
                    className="form-control"
                    name="viol_location_contact"
                    value={violation.viol_location_contact}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-3">
                  <label>Phone</label>
                  <input
                    className="form-control"
                    name="viol_location_phone"
                    value={violation.viol_location_phone}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="row my-3">
                <div className="col-md-3">
                  <label>Postal Code</label>
                  <input
                    className="form-control"
                    name="viol_location_postal"
                    value={violation.viol_location_postal}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-3">
                  <label>City</label>
                  <input
                    className="form-control"
                    name="viol_location_city"
                    value={violation.viol_location_city}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-3">
                  <label>Province</label>
                  <input
                    className="form-control"
                    name="viol_location_province"
                    value={violation.viol_location_province}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-3">
                  <label>Country</label>
                  <input
                    className="form-control"
                    name="viol_location_country"
                    value={violation.viol_location_country}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Violation Notes */}
              <h6 className="mt-3">Violation Notes</h6>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm mb-2"
                onClick={() =>
                  setViolation({
                    ...violation,
                    viol_notes: [
                      ...violation.viol_notes,
                      { title: "", body: "", visible: true },
                    ],
                  })
                }
              >
                ➕ Add Note
              </button>
              {violation.viol_notes.map((n, idx) => (
                <div key={idx} className="mb-2 border p-2 rounded bg-light">
                  <input
                    className="form-control mb-1"
                    placeholder="Title"
                    value={n.title}
                    onChange={(e) => {
                      const updated = [...violation.viol_notes];
                      updated[idx].title = e.target.value;
                      setViolation({ ...violation, viol_notes: updated });
                    }}
                  />
                  <textarea
                    className="form-control mb-1"
                    placeholder="Body"
                    value={n.body}
                    onChange={(e) => {
                      const updated = [...violation.viol_notes];
                      updated[idx].body = e.target.value;
                      setViolation({ ...violation, viol_notes: updated });
                    }}
                  />
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={n.visible}
                        onChange={(e) => {
                          const updated = [...violation.viol_notes];
                          updated[idx].visible = e.target.checked;
                          setViolation({ ...violation, viol_notes: updated });
                        }}
                      />
                      <label className="form-check-label">Visible</label>
                    </div>
                    {/* 🔹 Delete button for Note */}
                    <button
                      type="button"
                      className="btn btn-danger btn-sm mt-3"
                      onClick={() => {
                        const updated = violation.viol_notes.filter(
                          (_, i) => i !== idx
                        );
                        setViolation({ ...violation, viol_notes: updated });
                      }}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Reported By */}
            <div className="card p-3 mb-3">
              <h5>Reported By</h5>
              <div className="row my-3">
                <div className="col-md-4">
                  <label>Type</label>
                  <select
                    className="form-control"
                    name="viol_reported_by_type"
                    value={violation.viol_reported_by_type}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {reportedByTypeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label>Name</label>
                  <input
                    className="form-control"
                    name="viol_reported_by_name"
                    value={violation.viol_reported_by_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <label>ID Number</label>
                  <input
                    className="form-control"
                    name="viol_reported_by_id_number"
                    value={violation.viol_reported_by_id_number}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 mt-2">
                  <label>Phone</label>
                  <input
                    className="form-control"
                    name="viol_reported_by_phone"
                    value={violation.viol_reported_by_phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6 mt-2">
                  <label>Email</label>
                  <input
                    className="form-control"
                    name="viol_reported_by_email"
                    value={violation.viol_reported_by_email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <h6 className="mt-3">Reported Notes</h6>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm mb-2"
                onClick={() =>
                  setViolation({
                    ...violation,
                    viol_reported_notes: [
                      ...violation.viol_reported_notes,
                      { title: "", body: "" },
                    ],
                  })
                }
              >
                ➕ Add Note
              </button>

              {violation.viol_reported_notes.map((n, idx) => (
                <div key={idx} className="mb-2 border p-2 rounded bg-light">
                  <input
                    className="form-control mb-1"
                    placeholder="Title"
                    value={n.title}
                    onChange={(e) => {
                      const updated = [...violation.viol_reported_notes];
                      updated[idx].title = e.target.value;
                      setViolation({
                        ...violation,
                        viol_reported_notes: updated,
                      });
                    }}
                  />
                  <textarea
                    className="form-control"
                    placeholder="Body"
                    value={n.body}
                    onChange={(e) => {
                      const updated = [...violation.viol_reported_notes];
                      updated[idx].body = e.target.value;
                      setViolation({
                        ...violation,
                        viol_reported_notes: updated,
                      });
                    }}
                  />

                  {/* 🔹 Delete button for Reported Note */}
                  <div className="text-end">
                    <button
                      type="button"
                      className="btn btn-danger btn-sm mt-3"
                      onClick={() => {
                        const updated = violation.viol_reported_notes.filter(
                          (_, i) => i !== idx
                        );
                        setViolation({
                          ...violation,
                          viol_reported_notes: updated,
                        });
                      }}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Witnesses */}
            <div className="card p-3 mb-3">
              <h5>Witnesses</h5>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm mb-2"
                onClick={() =>
                  setViolation({
                    ...violation,
                    viol_witnesses: [
                      ...violation.viol_witnesses,
                      {
                        type: "",
                        name: "",
                        phone: "",
                        email: "",
                        witnesses_notes: [],
                      },
                    ],
                  })
                }
              >
                ➕ Add Witness
              </button>

              {violation.viol_witnesses.map((w, idx) => (
                <div key={idx} className="mb-3 border p-3 rounded">
                  <div className="row mb-2">
                    <div className="col-md-3">
                      <label>Type</label>
                      <select
                        className="form-control"
                        value={w.type || ""}
                        onChange={(e) => {
                          const updated = [...violation.viol_witnesses];
                          updated[idx].type = e.target.value;
                          setViolation({
                            ...violation,
                            viol_witnesses: updated,
                          });
                        }}
                      >
                        <option value="">Select</option>
                        {witnessTypeOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label>Name</label>
                      <input
                        className="form-control"
                        value={w.name || ""}
                        onChange={(e) => {
                          const updated = [...violation.viol_witnesses];
                          updated[idx].name = e.target.value;
                          setViolation({
                            ...violation,
                            viol_witnesses: updated,
                          });
                        }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label>Phone</label>
                      <input
                        className="form-control"
                        value={w.phone || ""}
                        onChange={(e) => {
                          const updated = [...violation.viol_witnesses];
                          updated[idx].phone = e.target.value;
                          setViolation({
                            ...violation,
                            viol_witnesses: updated,
                          });
                        }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label>Email</label>
                      <input
                        className="form-control"
                        value={w.email || ""}
                        onChange={(e) => {
                          const updated = [...violation.viol_witnesses];
                          updated[idx].email = e.target.value;
                          setViolation({
                            ...violation,
                            viol_witnesses: updated,
                          });
                        }}
                      />
                    </div>
                  </div>

                  {/* Witness Notes */}
                  <h6>Witness Notes</h6>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mb-2"
                    onClick={() => {
                      const updated = [...violation.viol_witnesses];
                      updated[idx].witnesses_notes =
                        updated[idx].witnesses_notes || [];
                      updated[idx].witnesses_notes.push({
                        title: "",
                        body: "",
                      });
                      setViolation({ ...violation, viol_witnesses: updated });
                    }}
                  >
                    ➕ Add Note
                  </button>

                  {(w.witnesses_notes || []).map((n, nIdx) => (
                    <div
                      key={nIdx}
                      className="mb-2 border p-2 rounded bg-light"
                    >
                      <input
                        className="form-control mb-1"
                        placeholder="Title"
                        value={n.title || ""}
                        onChange={(e) => {
                          const updated = [...violation.viol_witnesses];
                          updated[idx].witnesses_notes[nIdx].title =
                            e.target.value;
                          setViolation({
                            ...violation,
                            viol_witnesses: updated,
                          });
                        }}
                      />
                      <textarea
                        className="form-control"
                        placeholder="Body"
                        value={n.body || ""}
                        onChange={(e) => {
                          const updated = [...violation.viol_witnesses];
                          updated[idx].witnesses_notes[nIdx].body =
                            e.target.value;
                          setViolation({
                            ...violation,
                            viol_witnesses: updated,
                          });
                        }}
                      />

                      {/* 🔹 Delete Witness Note */}
                      <div className="text-end mt-3">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => {
                            const updated = [...violation.viol_witnesses];
                            updated[idx].witnesses_notes = updated[
                              idx
                            ].witnesses_notes.filter(
                              (_, noteIdx) => noteIdx !== nIdx
                            );
                            setViolation({
                              ...violation,
                              viol_witnesses: updated,
                            });
                          }}
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* 🔹 Delete Witness */}
                  <div className="text-end mt-4">
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => {
                        const updated = violation.viol_witnesses.filter(
                          (_, i) => i !== idx
                        );
                        setViolation({ ...violation, viol_witnesses: updated });
                      }}
                    >
                      Delete Witness
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Attachments */}
            <div className="card p-3 mb-3">
              <h5>Attachments</h5>

              {isNew ? (
                <div className="alert alert-info mb-0">
                  Please save the violation before uploading attachments.
                </div>
              ) : (
                <>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label>Attachment Type</label>
                      <select
                        className="form-control"
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="Accident Report">Accident Report</option>
                        <option value="Witness Statement">
                          Witness Statement
                        </option>
                        <option value="Photos">Photos</option>
                        <option value="Ticket">Ticket</option>
                        <option value="Insurance Claim">Insurance Claim</option>
                        <option value="Corrective Action">
                          Corrective Action
                        </option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label>Choose File</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) => {
                          if (e.target.files[0] && selectedType) {
                            const fileObj = e.target.files[0];
                            setAttachments((prev) => [
                              ...prev,
                              {
                                id: null,
                                file: fileObj,
                                name: fileObj.name,
                                type: selectedType,
                              },
                            ]);
                            setSelectedType("");
                            e.target.value = "";
                          }
                        }}
                      />
                    </div>
                  </div>

                  {pendingAttachments.length > 0 && (
                    <div className="mt-4 p-3 bg-light rounded border">
                      <div className="text-muted mb-2">Files Ready To Save</div>
                      <ul className="list-group">
                        {pendingAttachments.map((a, idx) => (
                          <li
                            key={`pending_${idx}`}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <span>
                              <a
                                href={URL.createObjectURL(a.file)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {a.file?.name || a.name || "Unnamed"}
                              </a>{" "}
                              ({a.type || "Unknown"})
                            </span>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                setAttachments((prev) =>
                                  prev.filter((item) => item !== a),
                                )
                              }
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {savedAttachments.length > 0 && (
                    <div className="mt-4 p-3 bg-light rounded border">
                      <div className="text-muted mb-2">Uploaded Files</div>
                      <ul className="list-group">
                        {savedAttachments.map((a) => (
                        <li
                          key={a.id}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <span>
                            <a
                              href={`${API_BASE}/uploads/violationattachments/${a.va_file}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {a.va_original}
                            </a>{" "}
                            ({a.type || "Unknown"})
                          </span>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={isDeletingAttachmentId === a.id}
                            onClick={() => handleDeleteSavedAttachment(a.id)}
                            title="Delete"
                          >
                            {isDeletingAttachmentId === a.id ? (
                              "Deleting..."
                            ) : (
                              <i className="bi bi-trash"></i>
                            )}
                          </button>
                        </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="d-flex justify-content-end">
              <button type="submit" className="btn btn-primary mt-3">
                Save
              </button>
            </div>
          </form>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default ViolationForm;
